import { useTranslation } from 'react-i18next'

import clsx from 'clsx'

import type { BookingPaymentOption } from 'pages/kyrgyzstan/booking/lib'

import type { PaymentStatus } from 'shared/api/kyrgyzstan/orders'
import { priceShow } from 'shared/lib/priceShow'
import { MBonusDiscount } from 'shared/ui/mbonus/mbonus-discount'
import { Paper } from 'shared/ui/paper'
import { Radio } from 'shared/ui/radio'
import { Skeleton } from 'shared/ui/skeleton'

import s from './styles.module.scss'

const SkeletonItemsCount = 2

const BookingPaymentMethodSkeleton = () => (
  <div className={s.paymentList}>
    {Array.from({ length: SkeletonItemsCount }).map((_, index) => (
      <div
        key={index}
        className={s.paymentSkeletonItem}
      >
        <Skeleton
          circle
          width={24}
          height={24}
          className={s.paymentSkeletonRadio}
        />

        <div className={s.paymentSkeletonInfo}>
          <Skeleton
            width="60%"
            height={16}
          />
          <Skeleton
            width="76%"
            height={12}
          />
        </div>
      </div>
    ))}
  </div>
)

interface BookingPaymentMethodCardProps {
  options: BookingPaymentOption[]
  selectedPayment?: PaymentStatus
  isDisabled?: boolean
  isLoading?: boolean
  onSelect: (value: PaymentStatus) => void
}

export const BookingPaymentMethodCard = ({
  options,
  selectedPayment,
  isDisabled = false,
  isLoading = false,
  onSelect,
}: BookingPaymentMethodCardProps) => {
  const { t } = useTranslation()

  return (
    <Paper
      padding={[16, 16]}
      radius="16px"
    >
      <div className={s.container}>
        <h2 className={clsx('bold-16', s.title)}>
          {t('kyrgyzstan_block.booking.subtitle')}
        </h2>

        {isLoading ? (
          <BookingPaymentMethodSkeleton />
        ) : (
          <div className={s.paymentList}>
            {options.map((option) => {
              const isSelected = selectedPayment === option.id

              return (
                <button
                  key={option.id}
                  type="button"
                  className={s.paymentItem}
                  aria-disabled={isDisabled}
                  onClick={() => {
                    if (isDisabled) return

                    onSelect(option.id)
                  }}
                >
                  <Radio
                    id={`payment-${option.id}`}
                    name="payment"
                    checked={isSelected}
                    disabled={isDisabled}
                  />

                  <div className={s.paymentInfo}>
                    <span className={clsx('bold-14', s.paymentTitle)}>
                      {option.title}
                    </span>

                    <span className={clsx('extra-bold-16', s.amount)}>
                      {priceShow(String(option.amount))} <span>&#8384;</span>
                    </span>

                    {option.description && (
                      <span className={clsx('medium-12', s.paymentDescription)}>
                        {option.description}
                      </span>
                    )}

                    {option.bonusText && (
                      <MBonusDiscount title={option.bonusText} />
                    )}
                  </div>
                </button>
              )
            })}
          </div>
        )}
      </div>
    </Paper>
  )
}
